import type {
  PublicTrustActivityItem,
  PublicTrustMeta,
  PublicTrustSnapshot,
  PublicTrustStats,
} from '../../types/publicTrust.ts';
import { formatRelativeHubDate } from '../../notificationsHub/formatHubDate.ts';

const countFormatter = new Intl.NumberFormat('es-AR');

export const TRUST_VISIBLE_AVATAR_SLOTS = 4;
export const TRUST_VISIBLE_AVATAR_SLOTS_MOBILE = 3;

export function getTrustVisibleAvatarSlotCount(isPhoneViewport: boolean): number {
  return isPhoneViewport ? TRUST_VISIBLE_AVATAR_SLOTS_MOBILE : TRUST_VISIBLE_AVATAR_SLOTS;
}

const TRUST_ACTIVE_90D_MIN_ABSOLUTE = 10;
const TRUST_ACTIVE_90D_MIN_SHARE_OF_VERIFIED = 0.2;
const TRUST_NEW_90D_MIN_ABSOLUTE = 5;

export function formatTrustCount(value: number): string {
  return countFormatter.format(Math.max(0, Math.floor(value)));
}

export function orderTrustActivityForDisplay(
  activity: PublicTrustSnapshot['activity']
): PublicTrustSnapshot['activity'] {
  return [...activity].sort((a, b) => {
    const aPortfolio = a.kind === 'portfolio' && a.subtype === 'servicio' ? 0 : 1;
    const bPortfolio = b.kind === 'portfolio' && b.subtype === 'servicio' ? 0 : 1;
    if (aPortfolio !== bPortfolio) return aPortfolio - bPortfolio;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

export type TrustAvatarSlot = {
  key: string;
  stackImage: string | null;
  profileImage: string | null;
  displayName: string;
  serviceTitle: string;
  categoryName: string | null;
  zone: string | null;
  relativeDate: string | null;
};

export function buildTrustAvatarSlots(
  activity: PublicTrustSnapshot['activity'],
  maxSlots = TRUST_VISIBLE_AVATAR_SLOTS
): TrustAvatarSlot[] {
  if (activity.length === 0 || maxSlots === 0) return [];

  return orderTrustActivityForDisplay(activity).slice(0, maxSlots).map((item) => ({
    key: `activity-${item.id}`,
    stackImage: item.coverImage,
    profileImage: item.user.imageProfile,
    displayName: item.user.displayName,
    serviceTitle: item.title,
    categoryName: item.userPrimaryCategory?.name ?? null,
    zone: item.zone,
    relativeDate: item.createdAt ? formatRelativeHubDate(item.createdAt) : null,
  }));
}

export function getTrustProfessionalsCount(stats: PublicTrustStats, meta: PublicTrustMeta): number {
  if (meta.scope === 'jurisdiction') return stats.professionalsInScope;
  return stats.professionalsVerified;
}

export function getTrustAvatarOverflowCount(
  stats: PublicTrustStats,
  meta: PublicTrustMeta,
  visibleSlotCount: number
): number {
  const total = getTrustProfessionalsCount(stats, meta);
  return Math.max(0, total - visibleSlotCount);
}

export function buildTrustCardHeadline(stats: PublicTrustStats, meta: PublicTrustMeta): string {
  const count = formatTrustCount(getTrustProfessionalsCount(stats, meta));

  if (meta.scope === 'jurisdiction' && meta.jurisdictionName) {
    return `${count} profesionales en ${meta.jurisdictionName} pueden ver tu obra`;
  }

  return `${count} profesionales verificados pueden ver tu obra`;
}

export function buildTrustCardSubhead(stats: PublicTrustStats, meta: PublicTrustMeta): string {
  if (meta.scope === 'jurisdiction' && meta.jurisdictionName) {
    return `En ${meta.jurisdictionName}, listos para enviarte presupuestos`;
  }

  if (stats.jurisdictionsCovered > 0) {
    return `En ${formatTrustCount(stats.jurisdictionsCovered)} provincias de Argentina, listos para cotizar tu obra`;
  }

  return 'Listos para recibir presupuestos de profesionales verificados';
}

export function buildTrustCardMobileLine(stats: PublicTrustStats, meta: PublicTrustMeta): string {
  const count = formatTrustCount(getTrustProfessionalsCount(stats, meta));

  if (meta.scope === 'jurisdiction' && meta.jurisdictionName) {
    return `${count} profesionales en ${meta.jurisdictionName} pueden ver tu obra`;
  }

  if (stats.jurisdictionsCovered > 0) {
    const provinces = formatTrustCount(stats.jurisdictionsCovered);
    return `${count} profesionales verificados en ${provinces} provincias`;
  }

  return `${count} profesionales verificados pueden ver tu obra`;
}

export function shouldShowTrustAvatars(meta: PublicTrustMeta): boolean {
  return !meta.hideActivity;
}

function shouldPromoteActive90dCount(stats: PublicTrustStats): boolean {
  const active = stats.professionalsActive90d;
  if (active < TRUST_ACTIVE_90D_MIN_ABSOLUTE) return false;

  const verified = stats.professionalsVerified;
  if (verified <= 0) return true;

  return active / verified >= TRUST_ACTIVE_90D_MIN_SHARE_OF_VERIFIED;
}

function shouldPromoteNew90dCount(stats: PublicTrustStats): boolean {
  const recent = stats.professionalsNew90d ?? 0;
  return recent >= TRUST_NEW_90D_MIN_ABSOLUTE;
}

export function buildTrustAvatarLead(
  stats: PublicTrustStats,
  meta: PublicTrustMeta,
  activityCount = 0
): string | null {
  if (meta.hideActivity) return null;

  if (shouldPromoteActive90dCount(stats)) {
    return `${formatTrustCount(stats.professionalsActive90d)} con actividad en los últimos 90 días`;
  }

  if (shouldPromoteNew90dCount(stats)) {
    return `${formatTrustCount(stats.professionalsNew90d ?? 0)} profesionales nuevos en los últimos 90 días`;
  }

  if (activityCount > 0 || meta.activityShown > 0) {
    return 'Publicaciones recientes de profesionales';
  }

  return 'Profesionales verificados en Edify';
}
